import React, { useState, useRef, useEffect } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiUrl, getAuthHeaders } from '../../config/api';
import { Eye, EyeOff, Play, Square, Copy, Check } from 'lucide-react';
import { 
  createPeerConnection, 
  createAnswer, 
  addIceCandidate, 
  closePeerConnection,
  SignalingClient 
} from '../../utils/webrtc';

export default function FanViewer() {
  const videoRef = useRef(null);
  const [accessCode, setAccessCode] = useState('');
  const [currentStream, setCurrentStream] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState('auto'); // 'auto', 'high', 'medium', 'low'
  const [connectionStats, setConnectionStats] = useState(null);
  
  // WebRTC state
  const [peerConnection, setPeerConnection] = useState(null);
  const peerConnectionRef = useRef(null);
  const [signalingClient, setSignalingClient] = useState(null);
  const signalingClientRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [broadcasterConnectionId, setBroadcasterConnectionId] = useState(null);
  const broadcasterConnectionIdRef = useRef(null);
  
  // Debug state
  const [debugLogs, setDebugLogs] = useState([]);
  
  const { hasFeature } = useSubscription();
  const { showSuccess, showError } = useNotification();

  // Debug logging function
  const addDebugLog = (message) => {
    console.log(message);
    setDebugLogs(prev => [...prev.slice(-10), `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // Auto-join from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const joinStreamId = urlParams.get('join');
    const joinCode = urlParams.get('code');
    
    if (joinStreamId && joinCode) {
      console.log('Auto-joining stream:', joinStreamId, 'with code:', joinCode);
      setAccessCode(joinCode);
      // Auto-join the stream
      joinStream(joinStreamId, joinCode);
    }
  }, []);

  // Debug initial state
  useEffect(() => {
    console.log('FanViewer component mounted');
    console.log('Initial currentStream:', currentStream);
    console.log('Initial accessCode:', accessCode);
  }, []);


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (peerConnectionRef.current) {
        closePeerConnection(peerConnectionRef.current);
      }
      if (signalingClientRef.current) {
        signalingClientRef.current.disconnect();
      }
    };
  }, []); // Empty dependency array - only run on unmount

  const joinStream = async (streamId = null, code = null) => {
    const accessCodeToUse = code || accessCode.trim();
    console.log('joinStream called with:', { streamId, code, accessCodeToUse });
    
    if (!accessCodeToUse) {
      console.log('No access code provided, returning');
      return;
    }

    setIsJoining(true);
    try {
      const requestBody = streamId 
        ? { streamId, accessCode: accessCodeToUse }
        : { accessCode: accessCodeToUse };
      
      console.log('Sending join request:', requestBody);
      
      const response = await fetch(getApiUrl('/api/streaming/join'), {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('Join response status:', response.status);
      const data = await response.json();
      console.log('Join response data:', data);
      
      if (response.ok) {
        console.log('Join successful, setting current stream:', data.stream);
        setCurrentStream(data.stream);
        if (data.accessGranted) {
          console.log('Access granted, setting up viewer connection');
          // Start WebRTC connection to view stream
          setupViewerConnection(data.stream);
        } else {
          console.log('Access not granted, starting polling');
          showSuccess('Access request sent to broadcaster. Please wait for approval.');
          // Start polling to check if access was granted
          startPollingForAccess(data.stream.id);
        }
      } else {
        console.error('Join failed:', data.error);
        showError(data.error || 'Failed to join stream');
      }
    } catch (error) {
      console.error('Error joining stream:', error);
      showError('Failed to join stream');
    } finally {
      setIsJoining(false);
    }
  };


  const startPollingForAccess = (streamId) => {
    console.log('Starting polling for access...');
    addDebugLog('Starting polling for access...');
    const pollInterval = setInterval(async () => {
      try {
        console.log('Polling for access...');
        addDebugLog('Polling for access...');
        // Re-call the join endpoint to check if access was granted
        const response = await fetch(getApiUrl('/api/streaming/join'), {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ accessCode: accessCode.trim() })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Polling response:', data);
          addDebugLog(`Polling response: accessGranted=${data.accessGranted}`);
          
          if (data.accessGranted) {
            console.log('Access granted! Setting up connection...');
            addDebugLog('Access granted! Setting up connection...');
            clearInterval(pollInterval);
            setCurrentStream(data.stream);
            setupViewerConnection(data.stream);
          }
        }
      } catch (error) {
        console.error('Error polling for access:', error);
        addDebugLog(`Polling error: ${error.message}`);
      }
    }, 2000); // Poll every 2 seconds
    
    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 300000);
  };

  const setupViewerConnection = async (stream) => {
    console.log('Setting up viewer connection for stream:', stream);
    
    if (!stream) {
      console.error('No stream data available for WebRTC setup');
      return;
    }

    try {
      // Get user ID from token
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.userId || payload.user_id || payload.id;

      console.log('Creating signaling client for viewer:', { streamId: stream.id, userId });

      // Create signaling client
      const client = new SignalingClient(stream.id, userId, token, 'viewer');
      
      // Set up message handlers
      client.onMessage = async (message) => {
        console.log('Received signaling message:', message.type, message);
        addDebugLog(`Received: ${message.type} from ${message.fromConnectionId || 'server'}`);

        switch (message.type) {
          case 'joined':
            console.log('Viewer joined, connectionId:', message.connectionId);
            client.connectionId = message.connectionId;
            break;
            
          case 'offer':
            console.log('Received offer from broadcaster');
            console.log('Offer message:', message);
            console.log('From connection ID:', message.fromConnectionId);
            addDebugLog(`Received offer from broadcaster: ${message.fromConnectionId}`);
            setBroadcasterConnectionId(message.fromConnectionId);
            broadcasterConnectionIdRef.current = message.fromConnectionId;
            if (peerConnectionRef.current) {
              console.log('Creating answer for offer');
              addDebugLog('Creating answer for offer');
              const answer = await createAnswer(peerConnectionRef.current, message.offer);
              console.log('Answer created:', answer);
              console.log('Sending answer back to broadcaster');
              addDebugLog('Sending answer back to broadcaster');
              // Send answer back to the broadcaster connection that sent the offer
              client.sendAnswer(answer, message.fromConnectionId);
              console.log('Answer sent to broadcaster');
              addDebugLog('Answer sent to broadcaster');
            } else {
              console.error('No peer connection available to handle offer');
              addDebugLog('ERROR: No peer connection available to handle offer');
            }
            break;
            
          case 'ice-candidate':
            console.log('Received ICE candidate');
            if (peerConnectionRef.current) {
              await addIceCandidate(peerConnectionRef.current, message.candidate);
            } else {
              console.error('No peer connection available to handle ICE candidate');
            }
            break;
            
          case 'broadcaster-joined':
            console.log('Broadcaster joined, peer connection should be ready');
            // Broadcaster is now available, peer connection should be ready
            break;
            
          case 'waiting-for-broadcaster':
            console.log('Waiting for broadcaster to start streaming');
            // Show waiting message to user
            showSuccess('Waiting for broadcaster to start streaming...');
            break;
        }
      };

      client.onError = (error) => {
        console.error('Signaling error:', error);
        showError('WebRTC connection error');
      };

      client.onClose = () => {
        setIsConnected(false);
      };

      // Create peer connection for viewing FIRST
      const pc = createPeerConnection(
        (candidate) => {
          // Send ICE candidate to broadcaster
          console.log('Sending ICE candidate from viewer');
          addDebugLog('Sending ICE candidate to broadcaster');
          // Send to the broadcaster using their connection ID
          if (broadcasterConnectionIdRef.current) {
            client.sendIceCandidate(candidate, broadcasterConnectionIdRef.current);
          } else {
            console.warn('No broadcaster connection ID available for ICE candidate');
            addDebugLog('WARNING: No broadcaster connection ID for ICE candidate');
          }
        },
        (stream) => {
          // Handle incoming video stream
          console.log('Received video stream in viewer:', stream);
          console.log('Stream tracks:', stream.getTracks().map(t => ({ kind: t.kind, label: t.label, enabled: t.enabled })));
          addDebugLog(`Received video stream: ${stream.getTracks().length} tracks`);
          
          if (videoRef.current) {
            console.log('Setting video srcObject');
            console.log('Video element before assignment:', {
              srcObject: videoRef.current.srcObject,
              readyState: videoRef.current.readyState,
              paused: videoRef.current.paused,
              currentTime: videoRef.current.currentTime
            });
            
            videoRef.current.srcObject = stream;

            // Set muted to true for mobile autoplay compatibility
            videoRef.current.muted = true;

            console.log('Video element after assignment:', {
              srcObject: videoRef.current.srcObject,
              readyState: videoRef.current.readyState,
              paused: videoRef.current.paused,
              currentTime: videoRef.current.currentTime,
              muted: videoRef.current.muted
            });

            // Force play the video with error handling for mobile
            const attemptPlay = async () => {
              if (!videoRef.current) return;

              try {
                // Ensure muted for autoplay
                videoRef.current.muted = true;

                await videoRef.current.play();
                console.log('✅ Video play() successful');

                // Try to unmute after successful play (optional, may fail on iOS)
                setTimeout(() => {
                  if (videoRef.current) {
                    videoRef.current.muted = false;
                    console.log('Unmuted video after successful play');
                  }
                }, 1000);
              } catch (error) {
                console.error('❌ Video play() failed:', error);
                // On mobile, user might need to tap the video to start playback
                showError('Tap the video to start playback');
              }
            };

            // Wait for stream to be ready
            setTimeout(attemptPlay, 100);
            
            console.log('Setting isConnected to true');
            addDebugLog('Setting isConnected to true');
            setIsConnected(true);
            showSuccess('Connected to stream!');
          } else {
            console.error('Video ref not available when receiving stream');
          }
        }
      );
      
      // Add connection state monitoring
      pc.onconnectionstatechange = () => {
        console.log('Viewer peer connection state:', pc.connectionState);
        if (pc.connectionState === 'connected') {
          console.log('✅ Viewer connected to broadcaster');
        } else if (pc.connectionState === 'failed') {
          console.log('❌ Viewer connection failed');
        } else if (pc.connectionState === 'closed') {
          console.log('🔒 Viewer connection closed');
        }
      };
      
      pc.oniceconnectionstatechange = () => {
        console.log('Viewer ICE connection state:', pc.iceConnectionState);
      };
      
      setPeerConnection(pc);
      peerConnectionRef.current = pc;
 

      // Connect to signaling server AFTER peer connection is ready
      console.log('Connecting to signaling server...');
      await client.connect();
      console.log('Signaling client connected');
      setSignalingClient(client);
      signalingClientRef.current = client;

      console.log('Viewer connection setup completed');
      
    } catch (error) {
      console.error('Error setting up WebRTC viewer connection:', error);
      showError('Failed to connect to video stream');
    }
  };

  const copyAccessCode = () => {
    navigator.clipboard.writeText(accessCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const leaveStream = async () => {
    // Clean up WebRTC connections
    if (peerConnection) {
      closePeerConnection(peerConnection);
      setPeerConnection(null);
    }
    
    if (signalingClient) {
      signalingClient.leave();
      signalingClient.disconnect();
      setSignalingClient(null);
    }
    
    setIsConnected(false);

    if (currentStream) {
      try {
        // Notify server that user is leaving
        await fetch(getApiUrl(`/api/streaming/${currentStream.id}/leave`), {
          method: 'POST',
          headers: getAuthHeaders()
        });
      } catch (error) {
        console.error('Error notifying server of leave:', error);
      }
    }
    
    setCurrentStream(null);
    // Keep the access code so user can easily rejoin
    // setAccessCode(''); // Removed this line to retain the code
  };

  if (!hasFeature('fan_viewing')) {
    return (
      <div className="text-center p-8">
        <Eye className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold mb-4">Fan View Not Available</h2>
        <p className="text-gray-600">You need a Fan View subscription to watch live streams.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col h-full">
      <div className="flex-shrink-0">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
          <Eye className="w-8 h-8" />
          Fan View
        </h1>
      </div>
      
      {!currentStream ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* Join with Access Code */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold mb-4">Join Stream with Access Code</h2>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="Enter access code"
                  className="flex-1 px-3 py-3 sm:py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center sm:text-left"
                  maxLength={8}
                />
                <button
                  onClick={() => joinStream()}
                  disabled={isJoining || !accessCode.trim()}
                  className="bg-blue-600 text-white px-6 py-3 sm:py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {isJoining ? 'Joining...' : 'Join Stream'}
                </button>
              </div>
              {accessCode && (
                <p className="text-xs text-gray-500 mt-2">
                  💡 Access code will be saved when you leave a stream
                </p>
              )}
              {accessCode && (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                  <span className="text-sm text-gray-600">Access Code: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{accessCode}</span></span>
                  <button
                    onClick={copyAccessCode}
                    className="text-blue-600 hover:text-blue-800 flex items-center self-start sm:self-auto"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-1 text-sm">{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Video Container - Responsive to viewport */}
          <div
            className="flex-1 bg-black rounded-lg overflow-hidden relative mb-4 min-h-0"
            style={{
              aspectRatio: '16/9'
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              controls
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%'
              }}
              className="w-full h-full"
              onLoadedMetadata={() => {
                console.log('Video metadata loaded');
                if (videoRef.current && videoRef.current.paused) {
                  videoRef.current.play().catch(err => {
                    console.error('Auto-play failed:', err);
                  });
                }
              }}
            />
            <div className="absolute top-4 left-4 bg-red-600 text-white px-2 py-1 rounded text-sm font-semibold flex items-center">
              <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
              LIVE
            </div>
            {!isConnected && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="text-center text-white">
                  <Eye className="w-12 h-12 mx-auto mb-2" />
                  <p>Connecting to stream...</p>
                  <p className="text-xs mt-2">isConnected: {isConnected.toString()}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Stream Info - Fixed height at bottom */}
          <div className="flex-shrink-0 bg-white p-4 rounded-lg shadow mb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold truncate">{currentStream.title}</h2>
                <p className="text-gray-600 truncate">{currentStream.description}</p>
                <p className="text-sm text-gray-500 mt-1">by {currentStream.broadcaster_name}</p>

                {/* Quality Selector */}
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Video Quality
                  </label>
                  <select
                    value={selectedQuality}
                    onChange={(e) => setSelectedQuality(e.target.value)}
                    className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="auto">Auto (Adaptive)</option>
                    <option value="high">High (Best Quality)</option>
                    <option value="medium">Medium (Balanced)</option>
                    <option value="low">Low (Save Data)</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    {selectedQuality === 'auto'
                      ? '🔄 Adjusts automatically based on your connection'
                      : selectedQuality === 'high'
                      ? '📶 Best quality, requires fast internet'
                      : selectedQuality === 'medium'
                      ? '⚖️ Good balance of quality and speed'
                      : '💾 Lower quality, works on slow connections'}
                  </p>
                </div>
              </div>
              <button
                onClick={leaveStream}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center justify-center flex-shrink-0 sm:ml-4"
              >
                <Square className="w-4 h-4 mr-2" />
                Leave Stream
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
